import { api } from "../api/client";
import { useData } from "../hooks/useData";
import FormDialog, { NEW_PREFIX, type FormValues } from "./FormDialog";
import type { Deal, Market, NewDealDraft, Org, Partner, Principal } from "../types";

const STAGES = ["qualifying", "engaged", "structuring", "deployment"];
const HEATS = ["critical", "high", "medium", "low"];
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Create an organisation if the user typed a new one; returns its id. */
export async function resolveOrg(value: string, type = "Operator"): Promise<string | null> {
  if (!value) return null;
  if (!value.startsWith(NEW_PREFIX)) return value;
  const name = value.slice(NEW_PREFIX.length).trim();
  if (!name) return null;
  const org = await api.post<Org>("/api/orgs", { name, type });
  return org.id;
}

export default function DealEditor({ deal, draft, partners, onClose, onSaved, onCreated }: {
  deal?: Deal;                 // edit an existing deal…
  draft?: NewDealDraft;        // …or create a new one, pre-filled (e.g. by Ask Saga)
  partners: Record<string, Partner>;
  onClose: () => void;
  onSaved: () => void;
  onCreated?: (id: string) => void;
}) {
  const { data: principals } = useData<Principal[]>("/api/principals");
  const { data: orgs } = useData<Org[]>("/api/orgs");
  const { data: markets } = useData<Market[]>("/api/markets");

  const src = (deal || {
    ...draft, id: "", owner: "", operator_id: draft?.operator_id || (draft?.counterparty_new ? NEW_PREFIX + draft.counterparty_new : ""),
  }) as Deal;
  const initial: FormValues = {
    name: src.name || "",
    principal_id: src.principal_id || "",
    operator_id: src.operator_id || "",
    market: src.market || "",
    stage: src.stage || "qualifying",
    heat: src.heat || "medium",
    owner: src.owner || "",
    value_m: src.value_m != null ? String(src.value_m) : "",
    probability: src.probability != null ? String(src.probability) : "",
    close_date: src.close_date ? String(src.close_date).slice(0, 10) : "",
    next_step: src.next_step || "",
    next_due: src.next_due ? String(src.next_due).slice(0, 10) : "",
    thesis: src.thesis || "",
  };

  const save = async (v: FormValues) => {
    const num = (s: string) => (s.trim() === "" ? null : Number(s));
    if (v.value_m && !Number.isFinite(Number(v.value_m))) throw new Error("Value must be a number (USD millions)");
    if (v.probability && !(Number(v.probability) >= 0 && Number(v.probability) <= 100)) throw new Error("Probability must be 0–100");
    let id = deal?.id;
    if (!id) {
      const created = await api.post<Deal>("/api/deals", { name: v.name.trim(), market: v.market || null, stage: v.stage, owner: v.owner || undefined });
      id = created.id;
    }
    await api.put(`/api/deals/${id}`, {
      name: v.name.trim(),
      principal_id: v.principal_id || null,
      operator_id: await resolveOrg(v.operator_id),
      market: v.market || null,
      stage: v.stage,
      heat: v.heat,
      owner: v.owner || null,
      value_m: num(v.value_m),
      probability: num(v.probability) ?? 0,
      close_date: v.close_date || null,
      next_step: v.next_step || null,
      next_due: v.next_due || null,
      thesis: v.thesis || null,
    });
    onSaved();
    onClose();
    if (!deal) onCreated?.(id);
  };

  const remove = async () => {
    if (!deal) return;
    if (!window.confirm(`Delete "${deal.name}"? It is removed from the pipeline (an admin can restore it from Settings).`)) return;
    await api.delete(`/api/deals/${deal.id}`);
    onSaved();
    onClose();
  };

  return (
    <FormDialog
      title={deal ? "Edit deal" : "New deal"}
      sub={deal ? deal.name : draft?.thesis ? "Filled in by Saga AI from what you wrote — check it, then create the deal." : undefined}
      saveLabel={deal ? "Save" : "Create deal"}
      initial={initial}
      onSave={save}
      onClose={onClose}
      onDelete={deal ? remove : undefined}
      fields={[
        { key: "name", label: "Deal", required: true, wide: true },
        { key: "principal_id", label: "Principal", type: "select", options: (principals || []).map(p => ({ value: p.id, label: p.name })) },
        { key: "operator_id", label: "Counterparty", type: "select", allowNew: "New organisation…",
          options: (orgs || []).filter(o => o.type !== "Principal").map(o => ({ value: o.id, label: o.name })) },
        { key: "market", label: "Market", type: "select", options: (markets || []).map(m => ({ value: m.name, label: `${m.flag || ""} ${m.name}`.trim() })) },
        { key: "stage", label: "Stage", type: "select", required: true, options: STAGES.map(s => ({ value: s, label: cap(s) })) },
        { key: "value_m", label: "Value (USD m)", type: "number", placeholder: "e.g. 4.2" },
        { key: "probability", label: "Probability %", type: "number", placeholder: "0–100" },
        { key: "owner", label: "Owner", type: "select", options: Object.values(partners).map(p => ({ value: p.id, label: p.name })) },
        { key: "heat", label: "Heat", type: "select", required: true, options: HEATS.map(h => ({ value: h, label: cap(h) })) },
        { key: "close_date", label: "Expected close", type: "date" },
        { key: "next_step", label: "Next step" },
        { key: "next_due", label: "Next step due", type: "date" },
        { key: "thesis", label: "Thesis", type: "textarea" },
      ]}
    />
  );
}
